import React, { FC, useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import { ChevronDown, Edit2, Search } from 'lucide-react';

import { IClientTab } from '../active-clients';
import { declOfNum } from '../../../utils/decOfNum';
import { AllSessions } from '../all-sessions';
import { SessionDetail, buildSessionDetailSession } from '../session-detail';
import { PrepHighlights } from '../prep-highlights';
import { ClientProfile } from '../ClientProfile';
import { TreatmentPlan } from '../treatment-plan';
import { Progress } from '../progress';
import { Supervision } from '../supervision';

const CLIENTS_PAGE_SIZE = 10;
const CONSULTATIONS_PAGE_SIZE = 9;

export const ArchiveClients: FC<IClientTab> = ({
  clients,
  consultations,
  search,
  setSearch,
  activeClient,
  setActiveClient,
  onEditClient,
  onSessionDetailModeChange,
  pendingSessionDetail = null,
  onPendingSessionDetailHandled,
}) => {
  const [page, setPage] = useState(1);
  const [consultationsPage, setConsultationsPage] = useState(1);
  const [activeTab, setActiveTab] = useState<'all' | 'prep' | 'profile' | 'plan' | 'progress' | 'supervision'>('all');
  const [sessionDetail, setSessionDetail] = useState<{ consultationId: number; initialTab: string } | null>(null);

  const sortedClients = useMemo(() => {
    return [...clients].sort((a, b) => a.full_name.localeCompare(b.full_name, 'ru', { sensitivity: 'base' }));
  }, [clients]);

  const totalPages = useMemo(() => Math.ceil(sortedClients.length / CLIENTS_PAGE_SIZE), [sortedClients]);
  const pages = useMemo(() => Array.from({ length: totalPages }, (_, index) => index + 1), [totalPages]);

  const sortedConsultations = useMemo(() => {
    if (!consultations) return [];
    return [...consultations].sort(
      (a, b) => new Date(b.datetime_start).getTime() - new Date(a.datetime_start).getTime(),
    );
  }, [consultations]);

  const consultationsTotalPages = useMemo(
    () => Math.ceil(sortedConsultations.length / CONSULTATIONS_PAGE_SIZE),
    [sortedConsultations.length],
  );
  const consultationPages = useMemo(
    () => Array.from({ length: consultationsTotalPages }, (_, index) => index + 1),
    [consultationsTotalPages],
  );

  useEffect(() => {
    setPage(1);
  }, [sortedClients]);

  useEffect(() => {
    setConsultationsPage(1);
  }, [consultations]);

  useEffect(() => {
    if (activeTab !== 'all') {
      setSessionDetail(null);
    }
  }, [activeTab]);

  useEffect(() => {
    if (!activeClient) {
      return;
    }

    const index = sortedClients.findIndex((client) => client.id === activeClient);
    if (index === -1) {
      return;
    }

    const pageForClient = Math.floor(index / CLIENTS_PAGE_SIZE) + 1;
    setPage((prev) => (prev === pageForClient ? prev : pageForClient));
  }, [activeClient, sortedClients]);

  const paginatedClients = useMemo(() => {
    const start = (page - 1) * CLIENTS_PAGE_SIZE;
    return sortedClients.slice(start, start + CLIENTS_PAGE_SIZE);
  }, [sortedClients, page]);

  const paginatedConsultations = useMemo(() => {
    if (!sortedConsultations.length) return [];
    const start = (consultationsPage - 1) * CONSULTATIONS_PAGE_SIZE;
    return sortedConsultations.slice(start, start + CONSULTATIONS_PAGE_SIZE);
  }, [sortedConsultations, consultationsPage]);

  const tabs = [
    { key: 'all' as const, label: 'Все Сессии' },
    { key: 'prep' as const, label: 'Подготовка', wip: true },
    { key: 'profile' as const, label: 'Профиль клиента', wip: true },
    { key: 'plan' as const, label: 'План терапии', wip: true },
    { key: 'progress' as const, label: 'Прогресс', wip: true },
    { key: 'supervision' as const, label: 'Для супервизии', wip: true },
  ];

  const selectedClientData = useMemo(
    () => (activeClient ? (sortedClients.find((c) => c.id === activeClient) ?? null) : null),
    [activeClient, sortedClients],
  );

  const sessionDetailConsultation = useMemo(
    () => (sessionDetail ? sortedConsultations.find((c) => c.id === sessionDetail.consultationId) : undefined),
    [sortedConsultations, sessionDetail],
  );
  const isSessionDetailMode = activeTab === 'all' && sessionDetail !== null;

  useEffect(() => {
    onSessionDetailModeChange?.(isSessionDetailMode);

    return () => {
      onSessionDetailModeChange?.(false);
    };
  }, [isSessionDetailMode, onSessionDetailModeChange]);

  useEffect(() => {
    if (!pendingSessionDetail) {
      return;
    }

    const consultation = sortedConsultations.find((item) => item.id === pendingSessionDetail.consultationId);
    if (!consultation) {
      return;
    }

    setActiveTab('all');
    setSessionDetail({
      consultationId: pendingSessionDetail.consultationId,
      initialTab: pendingSessionDetail.initialTab,
    });
    onPendingSessionDetailHandled?.();
  }, [onPendingSessionDetailHandled, pendingSessionDetail, sortedConsultations]);

  return (
    <div className="h-full w-full min-w-0 flex flex-col lg:flex-row bg-grey5 pb-6 pt-4 gap-4 overflow-x-hidden">
      {!isSessionDetailMode && (
        <div className="flex flex-col gap-5 w-64 shrink-0 pb-6 lg:pb-0 border-b border-b-greyBase40 lg:border-b-0">
          <div className="w-full bg-white border border-gray-200 rounded-xl p-4 h-fit">
            <div className="relative mb-4">
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Поиск клиента..."
                className="w-full pl-3 pr-8 py-2 border border-gray-200 rounded-lg text-sm outline-none focus:border-gray-400"
              />
              <Search
                size={14}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
              />
            </div>

            <div className="flex items-center justify-between text-xs text-gray-400 mb-2 px-1">
              <span>Найдено:</span>
              <span>
                {clients.length} {declOfNum(clients.length, ['клиент', 'клиента', 'клиентов'])}
              </span>
            </div>

            <div className="space-y-2">
              {paginatedClients.map((client) => (
                <div
                  key={client.id}
                  onClick={() => setActiveClient(client.id)}
                  className={classNames(
                    'w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-sm transition-colors cursor-pointer gap-2',
                    activeClient === client.id
                      ? 'bg-[#C41230] text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200',
                  )}
                >
                  <span className="min-w-0 flex-1 truncate text-left" title={client.full_name}>
                    {client.full_name}
                  </span>
                  {activeClient === client.id && (
                    <button
                      type="button"
                      className="flex shrink-0 items-center justify-center text-inherit hover:opacity-80 cursor-pointer transition-opacity"
                      onClick={(event) => {
                        event.stopPropagation();
                        setActiveClient(client.id);
                        onEditClient(client);
                      }}
                      aria-label="Редактировать клиента"
                    >
                      <Edit2 size={12} className="shrink-0" />
                    </button>
                  )}
                </div>
              ))}
            </div>

            {totalPages > 1 && (
              <div className="mt-5 flex w-full flex-wrap justify-center gap-2">
                {pages.map((pageNumber) => (
                  <button
                    key={pageNumber}
                    type="button"
                    className={classNames(
                      'min-w-[32px] rounded-[8px] border px-2 py-1 text-sm transition-colors cursor-pointer',
                      {
                        'bg-[#C41230] text-white border-[#C41230]': pageNumber === page,
                        'border-greyBase40 text-greyBase hover:text-blackBase hover:border-greyBase':
                          pageNumber !== page,
                      },
                    )}
                    onClick={(event) => {
                      event.stopPropagation();
                      setPage(pageNumber);
                    }}
                  >
                    {pageNumber}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
      <div className="flex-1 min-w-0 overflow-visible">
        <div className="flex items-center justify-between mb-4">
          {!isSessionDetailMode && (
            <div className="flex items-center gap-3 min-w-0">
              <h2 className="font-comfortaa font-bold text-xl text-blackBase truncate">
                {selectedClientData?.full_name ?? 'Выберите клиента'}
              </h2>
              <button type="button" className="text-greyBase hover:text-blackBase cursor-pointer p-1" aria-label="Меню">
                •••
              </button>
            </div>
          )}
          <div className={classNames('text-sm text-blackBase shrink-0', isSessionDetailMode ? 'ml-auto' : 'ml-2')}>
            {`${sortedConsultations.length} ${declOfNum(sortedConsultations.length || 0, ['сессия', 'сессии', 'сессий'])}`}
          </div>
        </div>

        {!isSessionDetailMode && (
          <div className="mb-5 flex flex-wrap items-center gap-1 overflow-visible rounded-xl border border-gray-200 bg-white px-3 py-2">
            {tabs.map((tab) => (
              <div key={tab.key} className="group relative">
                <button
                  type="button"
                  onClick={() => setActiveTab(tab.key)}
                  className={classNames(
                    'flex items-center gap-1 whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition-colors cursor-pointer',
                    {
                      'bg-amber-400 text-white': activeTab === tab.key,
                      'text-gray-600 hover:bg-gray-100 hover:text-gray-900': activeTab !== tab.key,
                    },
                  )}
                >
                  {tab.label}
                  {tab.key === 'supervision' && (
                    <ChevronDown size={12} className="inline shrink-0 opacity-90" aria-hidden />
                  )}
                  {tab.wip && <span className="ml-0.5 text-xs leading-none">🚧</span>}
                </button>

                {tab.wip && (
                  <div className="pointer-events-none absolute bottom-full left-1/2 z-50 mb-2 hidden -translate-x-1/2 group-hover:flex">
                    <div className="rounded-lg bg-gray-800 px-3 py-1.5 text-xs whitespace-nowrap text-white shadow-lg">
                      Раздел в разработке — показан пример
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {activeTab === 'all' ? (
          <>
            {sessionDetail && sessionDetailConsultation ? (
              <SessionDetail
                consultationId={sessionDetail.consultationId}
                session={buildSessionDetailSession(sessionDetailConsultation)}
                initialTab={sessionDetail.initialTab}
                clientName={selectedClientData?.full_name}
                onBack={() => setSessionDetail(null)}
              />
            ) : sessionDetail ? (
              <div className="rounded-2xl bg-white p-5 text-center text-sm text-gray-500 shadow-[0px_10px_30px_rgba(0,0,0,0.04)]">
                Сессия не найдена в списке
              </div>
            ) : (
              <>
                <AllSessions
                  consultations={paginatedConsultations}
                  onOpenSessionDetail={(consultationId, initialReportLabel) =>
                    setSessionDetail({ consultationId, initialTab: initialReportLabel })
                  }
                />
                {consultationsTotalPages > 1 && (
                  <div className="mt-5 flex w-full flex-wrap justify-center gap-2">
                    {consultationPages.map((pageNumber) => (
                      <button
                        key={pageNumber}
                        type="button"
                        className={classNames(
                          'min-w-[32px] rounded-[8px] border px-2 py-1 text-sm transition-colors cursor-pointer',
                          {
                            'bg-[#C41230] text-white border-[#C41230]': pageNumber === consultationsPage,
                            'border-greyBase40 text-greyBase hover:text-blackBase hover:border-greyBase':
                              pageNumber !== consultationsPage,
                          },
                        )}
                        onClick={() => setConsultationsPage(pageNumber)}
                      >
                        {pageNumber}
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
          </>
        ) : activeTab === 'prep' ? (
          <PrepHighlights clientName={selectedClientData?.full_name} />
        ) : activeTab === 'profile' ? (
          <ClientProfile />
        ) : activeTab === 'plan' ? (
          <TreatmentPlan />
        ) : activeTab === 'progress' ? (
          <Progress />
        ) : activeTab === 'supervision' ? (
          <Supervision />
        ) : (
          <div className="rounded-2xl bg-white p-5 text-center text-sm text-gray-500 shadow-[0px_10px_30px_rgba(0,0,0,0.04)]">
            Раздел в разработке
          </div>
        )}
      </div>
    </div>
  );
};
